import { Box, Button, Flex, Popover, Portal, Stack, Text } from "@chakra-ui/react";
import { AlertTriangle } from "lucide-react";
import { useState, type ReactElement, type ReactNode } from "react";

type PopConfirmProps = {
  trigger: ReactElement;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  confirmAriaLabel?: string;
  cancelAriaLabel?: string;
  onConfirm: () => Promise<void> | void;
};

/**
 * An anchored confirmation for destructive, row-scoped actions.
 *
 * The popover remains open when the action fails so the server error stays in
 * context. It closes only after a successful confirmation or an explicit
 * cancel, avoiding native browser dialogs and optimistic destructive UI.
 */
export function PopConfirm({
  trigger,
  title,
  description,
  confirmLabel = "Delete",
  confirmAriaLabel,
  cancelAriaLabel = "Cancel deletion",
  onConfirm,
}: PopConfirmProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    setPending(true);
    setError("");
    try {
      await onConfirm();
      setOpen(false);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The server could not complete this deletion.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={({ open: nextOpen }) => {
        if (pending && !nextOpen) return;
        setOpen(nextOpen);
        if (nextOpen) setError("");
      }}
      positioning={{ placement: "bottom-end", gutter: 8 }}
    >
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Portal>
        <Popover.Positioner zIndex="popover">
          <Popover.Content
            width={{ base: "min(320px, calc(100vw - 24px))", md: "320px" }}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="4px"
            bg="bg.panel"
            boxShadow="0 18px 48px rgba(0,0,0,.42)"
            overflow="hidden"
          >
            <Popover.Arrow>
              <Popover.ArrowTip borderColor="border.default" bg="bg.panel" />
            </Popover.Arrow>
            <Popover.Body p="4">
              <Stack gap="3.5">
                <Flex gap="3" align="start">
                  <Flex
                    boxSize="8"
                    flexShrink="0"
                    align="center"
                    justify="center"
                    borderRadius="4px"
                    bg="rgba(244,63,94,.12)"
                    color="status.negative"
                  >
                    <Box as={AlertTriangle} boxSize="4" />
                  </Flex>
                  <Box minW="0">
                    <Popover.Title fontSize="sm" fontWeight="800" lineHeight="1.35">
                      {title}
                    </Popover.Title>
                    <Popover.Description mt="1.5" color="fg.muted" fontSize="xs" lineHeight="1.55">
                      {description}
                    </Popover.Description>
                  </Box>
                </Flex>

                {error ? (
                  <Box
                    role="alert"
                    borderWidth="1px"
                    borderColor="rgba(244,63,94,.28)"
                    borderRadius="4px"
                    bg="rgba(244,63,94,.08)"
                    px="3"
                    py="2"
                  >
                    <Text color="status.negative" fontSize="xs" lineHeight="1.45">
                      {error}
                    </Text>
                  </Box>
                ) : null}

                <Flex justify="end" gap="2">
                  <Button
                    aria-label={cancelAriaLabel}
                    size="xs"
                    variant="ghost"
                    disabled={pending}
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    aria-label={confirmAriaLabel ?? confirmLabel}
                    size="xs"
                    bg="status.negative"
                    color="white"
                    loading={pending}
                    loadingText="Deleting..."
                    onClick={() => void confirm()}
                    _hover={{ filter: "brightness(1.08)" }}
                  >
                    {confirmLabel}
                  </Button>
                </Flex>
              </Stack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
